import { useState } from "react";
import { motion } from "framer-motion";
import { Database, Play, CheckCircle, AlertCircle, Loader } from "lucide-react";
import { 
  migrateActiveFieldForAllWorkspaces, 
  migrateActiveFieldForWorkspace
} from "../utils/migrateActiveField";

export default function MigrationAdmin() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [workspaceInput, setWorkspaceInput] = useState("");

  const handleMigrateAll = async () => {
    const confirm = window.confirm(
      "⚠️ This will add 'active: true' to ALL users across ALL workspaces. Continue?"
    );
    
    if (!confirm) return;

    setLoading(true);
    setResult(null);
    try {
      const data = await migrateActiveFieldForAllWorkspaces();
      setResult(data);
      if (data.success) {
        console.log(`✅ Migration successful! Updated ${data.totalUsersUpdated}/${data.totalUsersChecked} users.`);
      } else {
        console.error(`❌ Migration failed: ${data.error || data.message}`);
      }
    } catch (error) {
      console.error("❌ Migration error:", error);
      console.error("❌ Migration failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMigrateWorkspace = async () => {
    if (!workspaceInput.trim()) {
      console.warn("⚠️ Please enter a workspace ID");
      return;
    }

    const confirm = window.confirm(
      `⚠️ This will add 'active: true' to all users in workspace: ${workspaceInput}. Continue?`
    );
    
    if (!confirm) return;

    setLoading(true);
    setResult(null);
    try {
      const data = await migrateActiveFieldForWorkspace(workspaceInput.trim());
      setResult(data);
      if (data.success) {
        console.log(`✅ Migration successful! Updated ${data.totalUpdated}/${data.totalChecked} users in ${data.workspaceId}.`);
      } else {
        console.error(`❌ Migration failed: ${data.error}`);
      }
    } catch (error) {
      console.error("❌ Migration error:", error);
      console.error("❌ Migration failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Database className="w-8 h-8 text-indigo-600" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Database Migration Tool
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Add 'active' field to all users across workspaces
          </p>
        </div>
      </div>

      {/* Warning Banner */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 flex items-start gap-3"
      >
        <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 mt-0.5 flex-shrink-0" />
        <div>
          <h3 className="font-semibold text-yellow-900 dark:text-yellow-200">
            Important: One-Time Migration
          </h3>
          <p className="text-sm text-yellow-800 dark:text-yellow-300 mt-1">
            This tool adds the 'active: true' field to users that don't have it. 
            This is typically a one-time operation. Make sure to backup your data before running migration.
          </p>
        </div>
      </motion.div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 gap-6">
        {/* Migrate All Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-gray-900 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center gap-3 mb-4">
            <Play className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Migrate All Workspaces
            </h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Add 'active' field to ALL users across ALL workspaces
          </p>
          <button
            onClick={handleMigrateAll}
            disabled={loading}
            className="w-full px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Migrating...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Run Migration
              </>
            )}
          </button>
        </motion.div>
      </div>

      {/* Migrate Single Workspace Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white dark:bg-gray-900 rounded-xl shadow-md p-6 border border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center gap-3 mb-4">
          <Database className="w-6 h-6 text-purple-600" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Migrate Single Workspace
          </h2>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Add 'active' field to users in a specific workspace only
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Enter workspace ID (e.g., blind-cv)"
            value={workspaceInput}
            onChange={(e) => setWorkspaceInput(e.target.value)}
            className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none dark:bg-gray-800 dark:text-white"
          />
          <button
            onClick={handleMigrateWorkspace}
            disabled={loading || !workspaceInput.trim()}
            className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg transition flex items-center gap-2"
          >
            {loading ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Play className="w-5 h-5" />
                Migrate
              </>
            )}
          </button>
        </div>
      </motion.div>

      {/* Migration Results */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-xl shadow-md p-6 border ${
            result.success
              ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
              : "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
          }`}
        >
          <div className="flex items-center gap-3 mb-4">
            {result.success ? (
              <CheckCircle className="w-6 h-6 text-green-600" />
            ) : (
              <AlertCircle className="w-6 h-6 text-red-600" />
            )}
            <h3 className="text-lg font-semibold">
              {result.success ? "Migration Successful" : "Migration Failed"}
            </h3>
          </div>

          {result.success ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Users Checked</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {result.totalUsersChecked || result.totalChecked}
                  </div>
                </div>
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Users Updated</div>
                  <div className="text-2xl font-bold text-green-600">
                    {result.totalUsersUpdated || result.totalUpdated}
                  </div>
                </div>
              </div>

              {result.details && (
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                    Details:
                  </h4>
                  <div className="space-y-2">
                    {result.details.map((detail, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center p-3 bg-white dark:bg-gray-800 rounded-lg"
                      >
                        <span className="text-gray-700 dark:text-gray-300">
                          {detail.workspace} ({detail.collection})
                        </span>
                        <span className="font-semibold text-green-600">
                          {detail.updated}/{detail.totalUsers} updated
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-red-600 dark:text-red-400">
              {result.error || result.message || "Unknown error occurred"}
            </div>
          )}
        </motion.div>
      )}

      {/* Instructions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          📖 How to Use This Tool
        </h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300">
          <li><strong>Choose Migration Type:</strong> Select "Migrate All Workspaces" or "Migrate Single Workspace"</li>
          <li><strong>Run Migration:</strong> Click "Run Migration" or "Migrate" button to add 'active: true' to users</li>
          <li><strong>Verify:</strong> Check Firebase Console to confirm changes were applied</li>
          <li><strong>Test Access:</strong> Try accessing projects with inactive users to verify access control</li>
        </ol>
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            💡 <strong>Tip:</strong> This is a one-time operation. Users that already have the 'active' field will be skipped.
          </p>
        </div>
      </motion.div>
    </div>
  );
}

